import "../../moneyReports.css"
import { useState, useEffect, useContext } from "react"
import AuthContext from "contexte/AuthContext"
import { useTranslation } from "react-i18next"
import DashboardLayout from "components/layout/dashboard/DashboardLayout"
import FilterComponent from "components/Filters/FilterComponent/FilterComponent"
import PageLoader from "components/pageLoader/PageLoader"
import Tableau from "components/DataComponents/Tableau/Tableau"
import { fetchDiscountsByDay } from "services/fetchMoneyReports"
import { RELEASE_DATE } from 'utils/date';
import { Amount, Count, EmptyState, ErrorState, Pager, ReportHeader, SummaryTiles, formatEur } from "../../MoneyReportParts"

// Réductions de frais (platform_fees.discount) par jour, une colonne par discount_source
const SOURCES = [
    { key: 'goliaths_plus', label: 'Goliaths Plus' },
    { key: 'paradox', label: 'Paradox' },
    { key: 'campaign', label: 'Campagnes' },
    { key: 'priority', label: 'Priorité (par ticker)' },
    { key: 'reduced_fees', label: 'Réduction globale' },
    { key: 'reduced_margin_fees', label: 'Réduction marge client' },
    { key: 'copy', label: 'Copy invest' },
    { key: 'other', label: 'Autre' },
]

const Discounts = () => {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();

    const [days, setDays] = useState([]);
    const [totals, setTotals] = useState(null);
    const [rowsCount, setRowsCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selectedDate, setSelectedDate] = useState({
        date1: { startDate: new Date(RELEASE_DATE), endDate: new Date() },
        date2: { startDate: null, endDate: null },
    });
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(0);

    const fetchData = async () => {
        try {
            setError(false);
            const response = await fetchDiscountsByDay({ user, limit, page, startDate: selectedDate.date1.startDate, endDate: selectedDate.date1.endDate });
            // la ligne Total (WITH ROLLUP) n'arrive qu'en tête de la première page
            const totalRow = response.find(row => row.index_type === 0);
            if (totalRow) setTotals(totalRow);
            else if (page === 0) setTotals(null);
            setDays(response.filter(row => row.index_type !== 0));
            setRowsCount(response.length);
        } catch (e) {
            console.log(e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (selectedDate.date1.startDate !== null && selectedDate.date1.endDate !== null) {
            fetchData();
        }
    }, [selectedDate, page, limit]);

    const handleDate = (newDate) => {
        setSelectedDate(newDate);
        setPage(0);
    }

    const handleLimit = (newLimit) => {
        setLimit(newLimit);
        setPage(0);
    }

    const tbody = days.map(item => ({
        data: [
            <span className="money_report_num money_report_date">{item.date_formatted}</span>,
            <Amount value={item.total} strong />,
            ...SOURCES.map(source => <Amount value={item[source.key]} />),
            <Count value={item.trades_count} />
        ]
    }));

    const tiles = totals ? [
        { label: 'Total sur la période', value: formatEur(totals.total), hint: `${totals.trades_count} frais réduits`, main: true },
        ...SOURCES.filter(source => Number(totals[source.key])).map(source => ({
            label: source.label,
            value: formatEur(totals[source.key]),
            hint: `${Math.round(Number(totals[source.key]) / Number(totals.total) * 100)} % du total`
        }))
    ] : [];

    return (
        <DashboardLayout page={t("pages_name.report_discounts")}>
            <div className="money_report_page">
                <ReportHeader
                    title="Réductions de frais"
                    hint="Montant des réductions accordées sur les frais de plateforme, par jour et par origine (abonnement, campagne, réduction manuelle...). Seuls les frais réussis sont comptés."
                />

                <div className="money_report_toolbar">
                    <FilterComponent
                        date={true}
                        limit={true}
                        fetchLimit={limit}
                        setFetchLimit={handleLimit}
                        selectedDate={selectedDate}
                        setSelectedDate={handleDate}
                    />
                </div>

                {
                    loading ? (
                        <div className='pageLoaderBox'>
                            <PageLoader />
                        </div>
                    ) : error ? (
                        <ErrorState title="Erreur lors du chargement des réductions" onRetry={fetchData} />
                    ) : (
                        <>
                            {totals && <SummaryTiles tiles={tiles} />}
                            <div className="money_report_card">
                                {
                                    tbody.length || page > 0 ? (
                                        <>
                                            <Tableau
                                                thead={['Date', 'Total', ...SOURCES.map(source => source.label), 'Nb frais réduits']}
                                                tbody={tbody}
                                            />
                                            <Pager page={page} setPage={setPage} hasNextPage={rowsCount >= limit} />
                                        </>
                                    ) : (
                                        <EmptyState title="Aucune réduction sur la période" hint="Élargissez les dates pour voir les réductions accordées." />
                                    )
                                }
                            </div>
                        </>
                    )
                }
            </div>
        </DashboardLayout>
    )
}

export default Discounts
